import React, { useMemo } from "react";
import { useConfig } from "@/context/ConfigContext";

const CONFETTI_COLORS = ["#2dd4bf", "#6366f1", "#f59e0b", "#f43f5e", "#34d399", "#38bdf8"];

// Each spec drives a non-interactive, behind-modal ambient particle layer.
const SPECS = {
  snow:     { count: 38, travel: "oe-fall", dur: [8, 16],  size: [4, 9],   wobble: true },
  leaves:   { count: 22, travel: "oe-fall", dur: [9, 16],  size: [16, 26], wobble: true, spin: true, glyphs: ["🍂", "🍁", "🌿"] },
  bats:     { count: 16, travel: "oe-fall", dur: [10, 18], size: [16, 28], flutter: true, glyphs: ["🦇"] },
  confetti: { count: 46, travel: "oe-fall", dur: [6, 12],  size: [6, 11],  spin: true, confetti: true },
  hearts:   { count: 20, travel: "oe-rise", dur: [8, 15],  size: [12, 22], wobble: true, glyphs: ["❤️", "💖", "💗", "💘"] },
};

const rand = (a, b) => a + Math.random() * (b - a);

export default function SeasonalEffect() {
  const { theme } = useConfig();
  const effect = theme?.effect;
  const spec = SPECS[effect];

  const particles = useMemo(() => {
    if (!spec) return [];
    return Array.from({ length: spec.count }).map((_, i) => ({
      i,
      left: rand(0, 100),
      size: rand(spec.size[0], spec.size[1]),
      dur: rand(spec.dur[0], spec.dur[1]),
      delay: -rand(0, spec.dur[1]),
      swayDur: rand(2, 5),
      spinDur: rand(4, 8),
      color: spec.confetti ? CONFETTI_COLORS[i % CONFETTI_COLORS.length] : null,
      glyph: spec.glyphs ? spec.glyphs[i % spec.glyphs.length] : null,
    }));
  }, [spec, effect]);

  if (!spec) return null;

  return (
    <div className="oe-fx" aria-hidden="true" data-testid="seasonal-effect" data-effect={effect}>
      {particles.map(p => (
        <span key={p.i} className="oe-p"
          style={{ left: `${p.left}%`, animationName: spec.travel, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s` }}>
          <span className="oe-inner"
            style={{ animation: spec.wobble ? `oe-sway ${p.swayDur}s ease-in-out infinite alternate`
              : spec.flutter ? `oe-flutter ${p.swayDur}s ease-in-out infinite alternate` : "none" }}>
            {p.glyph ? (
              <span style={{ fontSize: `${p.size}px`, display: "inline-block",
                animation: spec.spin ? `oe-spin ${p.spinDur}s linear infinite` : "none" }}>{p.glyph}</span>
            ) : spec.confetti ? (
              <span style={{ display: "inline-block", width: `${p.size}px`, height: `${p.size * 0.5}px`,
                background: p.color, borderRadius: "1px", animation: `oe-spin ${p.spinDur}s linear infinite` }} />
            ) : (
              <span style={{ display: "inline-block", width: `${p.size}px`, height: `${p.size}px`, borderRadius: "9999px",
                background: "rgba(148,163,184,.75)", boxShadow: "0 0 4px rgba(148,163,184,.4)" }} />
            )}
          </span>
        </span>
      ))}
    </div>
  );
}
